import React, { useState, useEffect } from "react";
import {
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Rating,
  Grid,
  Tabs,
  Tab,
  Box,
  IconButton,
  CircularProgress,
} from "@mui/material";
import {
  Visibility as VisibilityIcon,
  DriveFileRenameOutline as DriveFileRenameOutlineIcon,
  Chat as ChatIcon,
  Send as SendIcon,
  ConfirmationNumber,
  Description,
  Person,
  Phone,
  AssignmentInd,
  Schedule,
  Update,
  ChatBubbleOutline,
} from "@mui/icons-material";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonBadge from "components/ArgonBadge";
import ArgonButton from "components/ArgonButton";
import ArgonInput from "components/ArgonInput";
import { useParams, useNavigate } from "react-router-dom";
import api from "utils/api";
import TableComponent from "examples/Tables/Table";
import PaginationBar from "components/PaginationBar/PaginationBar";
import { toast } from "react-toastify";

// Premium Reusable Components
import PremiumCard from "components/PremiumCard/PremiumCard";
import PremiumDialog from "components/PremiumDialog/PremiumDialog";
import StatusBadge from "components/StatusBadge/StatusBadge";
import InfoRow from "components/InfoRow/InfoRow";
import MessageBubble from "components/MessageBubble/MessageBubble";
import PremiumSelect from "components/PremiumSelect/PremiumSelect";
import { Dropdown } from "components/CustomDropdown";

function communication() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [supportTickets, setSupportTickets] = useState([]);
  const [messages, setMessages] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  // Dialog states
  const [viewDialog, setViewDialog] = useState(false);
  const [statusDialog, setStatusDialog] = useState(false);
  const [messageDialog, setMessageDialog] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);

  // Message states
  const [ticketMessages, setTicketMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);

  // Admin states
  const [admins, setAdmins] = useState([]);
  const [selectedAdmin, setSelectedAdmin] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [updatingStatus, setUpdatingStatus] = useState(false);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  useEffect(() => {
    if (id) {
      fetchData();
    }
  }, [id]);

  const fetchData = async () => {
    try {
      setLoading(true);

      // Fetch support tickets
      const ticketsResponse = await api.get(`/support-tickets`, {
        params: { parentId: id },
      });
      const tickets = ticketsResponse.data.data || [];
      setSupportTickets(tickets);

      // Fetch messages for all tickets
      if (tickets.length > 0) {
        const messagePromises = tickets.map((ticket) => api.get(`/messages/ticket/${ticket._id}`));
        const messageResponses = await Promise.all(messagePromises);
        const allMessages = messageResponses.flatMap((res) => res.data.data || []);
        setMessages(allMessages);
      }

      // Fetch feedbacks from API (dynamic - fully integrated)
      const feedbacksResponse = await api.get(`/feedbacks`, {
        params: { parentId: id },
      });
      setReviews(feedbacksResponse.data.data || []);

      // Fetch admins for assignment
      const adminsResponse = await api.get(`/user`);
      const adminData = adminsResponse.data.data?.users || adminsResponse.data.data || [];
      setAdmins(Array.isArray(adminData) ? adminData : []);

      setLoading(false);
    } catch (error) {
      console.error("Error fetching data:", error);
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "OPEN":
        return "warning";
      case "CLOSED":
        return "success";
      case "ON_HOLD":
        return "info";
      case "TERMINATED":
        return "error";
      default:
        return "secondary";
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "Low":
        return "success";
      case "Medium":
        return "warning";
      case "High":
        return "error";
      default:
        return "secondary";
    }
  };

  // Dialog handlers
  const handleViewTicket = (ticket) => {
    setSelectedTicket(ticket);
    setViewDialog(true);
  };

  const handleEditTicket = (ticket) => {
    setSelectedTicket(ticket);
    setSelectedStatus(ticket.status);
    setSelectedAdmin(ticket.assignedTo?._id || "");
    setStatusDialog(true);
  };

  const handleMessageTicket = async (ticket) => {
    setSelectedTicket(ticket);
    setMessageDialog(true);
    await fetchTicketMessages(ticket._id);
  };

  // Fetch messages for a specific ticket
  const fetchTicketMessages = async (ticketId) => {
    setLoadingMessages(true);
    try {
      const response = await api.get(`/messages/ticket/${ticketId}`);
      setTicketMessages(response.data.data || []);
    } catch (error) {
      console.error("Error fetching messages:", error);
      setTicketMessages([]);
      toast.error("Failed to load messages");
    } finally {
      setLoadingMessages(false);
    }
  };

  // Send message
  const handleSendMessage = async () => {
    if (!newMessage.trim()) {
      toast.warning("Please enter a message");
      return;
    }

    setSendingMessage(true);
    try {
      const userStr = localStorage.getItem("user");
      if (!userStr) {
        toast.error("User not found. Please login again.");
        return;
      }

      const user = JSON.parse(userStr);
      await api.post(`/messages`, {
        message: newMessage,
        from: "ADMIN",
        userId: user._id,
        ticketId: selectedTicket._id,
      });

      setNewMessage("");
      await fetchTicketMessages(selectedTicket._id);
      toast.success("Message sent successfully");
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error(error.response?.data?.message || "Failed to send message");
    } finally {
      setSendingMessage(false);
    }
  };

  // Update ticket status
  const handleSaveStatus = async (e) => {
    e.preventDefault();
    setUpdatingStatus(true);
    try {
      const updateData = { status: selectedStatus };
      if (selectedAdmin) updateData.assignedTo = selectedAdmin;

      await api.put(`/support-tickets/${selectedTicket._id}`, updateData);
      toast.success("Ticket updated successfully");
      setStatusDialog(false);
      fetchData(); // Refresh data
    } catch (error) {
      console.error("Error updating ticket:", error);
      toast.error(error.response?.data?.message || "Failed to update ticket");
    } finally {
      setUpdatingStatus(false);
    }
  };

  // Prepare table data for support tickets
  const getSupportTicketTableData = () => {
    const startIndex = (currentPage - 1) * rowsPerPage;
    const paginatedTickets = supportTickets.slice(startIndex, startIndex + rowsPerPage);

    const columns = [
      // { name: "S.No", align: "center", width: "5%" },
      { name: "Subject", align: "left", width: "25%" },
      { name: "Priority", align: "center", width: "12%" },
      { name: "Status", align: "center", width: "12%" },
      { name: "Assigned To", align: "left", width: "20%" },
      { name: "Created At", align: "center", width: "14%" },
      { name: "Actions", align: "center", width: "12%" },
    ];

    const rows = paginatedTickets.map((ticket, index) => {
      const serialNumber = (currentPage - 1) * rowsPerPage + index + 1;
      const adminName = ticket.assignedTo?.fullName || "Not Assigned";
      const adminEmail = ticket.assignedTo?.email || "N/A";

      return {
        // "S.No": (
        //   <ArgonBox textAlign="center" px={2} py={0.5}>
        //     <ArgonTypography variant="caption" color="text" fontWeight="medium">
        //       {serialNumber}
        //     </ArgonTypography>
        //   </ArgonBox>
        // ),
        Subject: (
          <ArgonBox px={2} py={0.5}>
            <ArgonTypography
              variant="caption"
              color="text"
              fontWeight="medium"
              title={ticket.subject}
              noWrap
            >
              {ticket.subject}
            </ArgonTypography>
          </ArgonBox>
        ),
        Priority: (
          <ArgonBox display="flex" justifyContent="center" px={2} py={0.5}>
            <ArgonBadge
              variant="gradient"
              badgeContent={ticket.priority}
              color={getPriorityColor(ticket.priority)}
              size="xs"
              container
            />
          </ArgonBox>
        ),
        Status: (
          <ArgonBox display="flex" justifyContent="center" px={2} py={0.5}>
            <ArgonBadge
              variant="gradient"
              badgeContent={ticket.status}
              color={getStatusColor(ticket.status)}
              size="xs"
              container
            />
          </ArgonBox>
        ),
        "Assigned To": (
          <ArgonBox display="flex" alignItems="center" py={0.5}>
            <ArgonBox display="flex" flexDirection="column" minWidth={0} flex={1}>
              <ArgonTypography variant="caption" color="text" fontWeight="medium" noWrap>
                {adminName}
              </ArgonTypography>
              <ArgonTypography variant="caption" color="secondary" noWrap>
                {adminEmail}
              </ArgonTypography>
            </ArgonBox>
          </ArgonBox>
        ),
        "Created At": (
          <ArgonBox textAlign="center" px={2} py={0.5}>
            <ArgonTypography variant="caption" color="text" fontWeight="medium">
              {new Date(ticket.createdAt).toLocaleDateString()}
            </ArgonTypography>
          </ArgonBox>
        ),
        Actions: (
          <ArgonBox
            display="flex"
            alignItems="center"
            justifyContent="center"
            gap={1}
            px={2}
            py={0.5}
          >
            <IconButton
              size="small"
              color="info"
              onClick={() => handleViewTicket(ticket)}
              sx={{
                "&:hover": {
                  backgroundColor: "rgba(25, 118, 210, 0.08)",
                },
              }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              color="warning"
              onClick={() => handleEditTicket(ticket)}
              sx={{
                "&:hover": {
                  backgroundColor: "rgba(255, 152, 0, 0.08)",
                },
              }}
            >
              <DriveFileRenameOutlineIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              color="success"
              onClick={() => handleMessageTicket(ticket)}
              sx={{
                "&:hover": {
                  backgroundColor: "rgba(76, 175, 80, 0.08)",
                },
              }}
            >
              <ChatIcon fontSize="small" />
            </IconButton>
          </ArgonBox>
        ),
      };
    });

    return { columns, rows };
  };

  return (
    <ArgonBox p={1.5}>
      {/* Simple Header */}
      <ArgonBox mb={2} mt={1} textAlign="center">
        <ArgonTypography variant="h6" fontWeight="bold" color="primary" mb={0.5}>
          💬 Communication
        </ArgonTypography>
      </ArgonBox>

      {/* Tabs */}
      <Card sx={{ mb: 2 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{
            "& .MuiTab-root": {
              fontSize: "0.85rem",
              fontWeight: "medium",
              textTransform: "none",
            },
          }}
        >
          <Tab label="🎫 Support Tickets" />
          <Tab label="⭐ Feedback" />
        </Tabs>
      </Card>

      {loading ? (
        <ArgonBox textAlign="center" py={5}>
          <ArgonTypography variant="body2" color="text.secondary">
            Loading...
          </ArgonTypography>
        </ArgonBox>
      ) : (
        <>
          {/* Support Tickets Tab */}
          {activeTab === 0 && (
            <ArgonBox>
              {supportTickets.length === 0 ? (
                <Card
                  sx={{
                    borderRadius: 2,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                    border: "1px solid #e0e0e0",
                    p: 2,
                  }}
                >
                  <ArgonBox textAlign="center">
                    <ArgonTypography variant="caption" color="text.secondary">
                      No support tickets yet 📭
                    </ArgonTypography>
                  </ArgonBox>
                </Card>
              ) : (
                <ArgonBox>
                  <Card
                    sx={{
                      borderRadius: 2,
                      boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                      border: "1px solid #e0e0e0",
                    }}
                  >
                    <ArgonBox
                      sx={{
                        "& .MuiTableRow-root:not(:last-child)": {
                          "& td": {
                            borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                              `${borderWidth[1]} solid ${borderColor}`,
                          },
                        },
                        "& .MuiTableCell-root": {
                          padding: "8px 16px",
                          verticalAlign: "middle",
                        },
                        "& .MuiTableCell-head": {
                          fontWeight: "600",
                          textTransform: "uppercase",
                          fontSize: "0.75rem",
                          letterSpacing: "0.5px",
                        },
                      }}
                    >
                      <TableComponent
                        columns={getSupportTicketTableData().columns}
                        rows={getSupportTicketTableData().rows}
                      />
                    </ArgonBox>
                  </Card>

                  {supportTickets.length > rowsPerPage && (
                    <ArgonBox mt={2}>
                      <PaginationBar
                        currentPage={currentPage}
                        rowsPerPage={rowsPerPage}
                        totalItems={supportTickets.length}
                        onPageChange={(e, page) => setCurrentPage(page)}
                        onRowsPerPageChange={(e) => {
                          setRowsPerPage(e.target.value);
                          setCurrentPage(1);
                        }}
                      />
                    </ArgonBox>
                  )}
                </ArgonBox>
              )}
            </ArgonBox>
          )}

          {/* Feedback Tab */}
          {activeTab === 1 && (
            <ArgonBox>
              {reviews.length === 0 ? (
                <Card
                  sx={{
                    borderRadius: 2,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                    border: "1px solid #e0e0e0",
                    p: 2,
                  }}
                >
                  <ArgonBox textAlign="center">
                    <ArgonTypography variant="caption" color="text.secondary">
                      No feedback yet ✨
                    </ArgonTypography>
                  </ArgonBox>
                </Card>
              ) : (
                reviews.map((review, index) => (
                  <Card
                    key={index}
                    sx={{
                      borderRadius: 2,
                      boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                      border: "1px solid #e0e0e0",
                      overflow: "hidden",
                      background: "#ffffff",
                      mb: 2,
                    }}
                  >
                    <ArgonBox sx={{ p: 1.5 }}>
                      <ArgonBox
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        mb={1}
                      >
                        <ArgonBox display="flex" alignItems="center" gap={1}>
                          <Rating value={review.rating || 0} readOnly size="small" />
                          <ArgonTypography
                            variant="caption"
                            fontWeight="medium"
                            color="text.primary"
                          >
                            {review.rating || 0}/5
                          </ArgonTypography>
                        </ArgonBox>
                        <Chip
                          label={review.type}
                          size="small"
                          color="info"
                          sx={{ height: 20, fontSize: "0.7rem" }}
                        />
                      </ArgonBox>

                      <ArgonTypography
                        variant="body2"
                        color="text.primary"
                        fontSize="0.85rem"
                        mb={1}
                      >
                        {review.description || review.review}
                      </ArgonTypography>

                      <ArgonBox display="flex" justifyContent="space-between" alignItems="center">
                        <ArgonTypography variant="caption" color="text.secondary" fontSize="0.7rem">
                          {review.parentId?.fullName || review.studentId?.fullName || "N/A"}
                        </ArgonTypography>
                        <ArgonTypography variant="caption" color="text.secondary" fontSize="0.7rem">
                          {new Date(review.createdAt).toLocaleDateString("en-GB")}
                        </ArgonTypography>
                      </ArgonBox>
                    </ArgonBox>
                  </Card>
                ))
              )}
            </ArgonBox>
          )}
        </>
      )}

      {/* ==================== VIEW TICKET DIALOG ==================== */}
      <PremiumDialog
        open={viewDialog}
        onClose={() => {
          setViewDialog(false);
          setSelectedTicket(null);
        }}
        title="Ticket Details"
        icon={ConfirmationNumber}
        maxWidth="md"
      >
        {selectedTicket && (
          <>
            {/* Main Ticket Info */}
            <PremiumCard gradient="purple" sx={{ p: 3, mb: 3 }}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <InfoRow
                    icon={Description}
                    label="Subject"
                    value={selectedTicket.subject}
                    white
                  />
                </Grid>

                {selectedTicket.description && (
                  <Grid item xs={12}>
                    <InfoRow
                      icon={Description}
                      label="Description"
                      value={selectedTicket.description}
                      white
                    />
                  </Grid>
                )}

                <Grid item xs={12} md={6}>
                  <InfoRow
                    icon={Person}
                    label="Created By"
                    value={
                      selectedTicket.createdBy === "PARENT"
                        ? selectedTicket.parentId?.fullName || "Unknown Parent"
                        : selectedTicket.tutorId?.fullName || "Unknown Tutor"
                    }
                    white
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <InfoRow
                    icon={Phone}
                    label="Contact"
                    value={selectedTicket.parentId?.phone || selectedTicket.tutorId?.phone || "N/A"}
                    white
                  />
                </Grid>
              </Grid>
            </PremiumCard>

            {/* Status & Assignment Info */}
            <PremiumCard elevated sx={{ p: 3, mb: 3 }}>
              <ArgonTypography variant="h6" fontWeight="bold" mb={3} color="#334155">
                Status & Assignment
              </ArgonTypography>

              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <ArgonBox mb={1}>
                    <ArgonTypography variant="caption" color="#64748b" fontWeight="bold">
                      STATUS
                    </ArgonTypography>
                  </ArgonBox>
                  <StatusBadge status={selectedTicket.status} type="status" />
                </Grid>

                <Grid item xs={12} md={4}>
                  <ArgonBox mb={1}>
                    <ArgonTypography variant="caption" color="#64748b" fontWeight="bold">
                      PRIORITY
                    </ArgonTypography>
                  </ArgonBox>
                  <StatusBadge status={selectedTicket.priority} type="priority" />
                </Grid>

                <Grid item xs={12} md={4}>
                  <InfoRow
                    icon={AssignmentInd}
                    label="Assigned To"
                    value={selectedTicket.assignedTo?.fullName || "Not Assigned"}
                    iconColor="#10b981"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <InfoRow
                    icon={Schedule}
                    label="Created At"
                    value={new Date(selectedTicket.createdAt).toLocaleString()}
                    iconColor="#8b5cf6"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <InfoRow
                    icon={Update}
                    label="Updated At"
                    value={new Date(selectedTicket.updatedAt).toLocaleString()}
                    iconColor="#ef4444"
                  />
                </Grid>
              </Grid>
            </PremiumCard>

            {/* Actions */}
            <ArgonBox display="flex" justifyContent="flex-end" gap={2}>
              <ArgonButton
                variant="outlined"
                color="secondary"
                onClick={() => {
                  setViewDialog(false);
                  setSelectedTicket(null);
                }}
              >
                Close
              </ArgonButton>
            </ArgonBox>
          </>
        )}
      </PremiumDialog>

      {/* ==================== UPDATE STATUS DIALOG ==================== */}
      <PremiumDialog
        open={statusDialog}
        onClose={() => {
          setStatusDialog(false);
          setSelectedTicket(null);
          setSelectedStatus("");
          setSelectedAdmin("");
        }}
        title="Update Ticket"
        icon={AssignmentInd}
        maxWidth="sm"
        headerGradient="linear-gradient(135deg, #10b981 0%, #059669 100%)"
      >
        <form onSubmit={handleSaveStatus}>
          <PremiumCard elevated sx={{ p: 3, mb: 3 }}>
            <PremiumSelect
              label="Status"
              value={selectedStatus}
              options={["OPEN", "CLOSED", "ON_HOLD", "TERMINATED"]}
              onChange={(e) => setSelectedStatus(e.target.value)}
              required
              placeholder="Select Status"
            />

            <ArgonBox>
              <ArgonTypography
                variant="caption"
                fontWeight="bold"
                sx={{
                  color: "#64748b",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  mb: 1,
                  display: "block",
                }}
              >
                Assign to Admin
              </ArgonTypography>
              <select
                value={selectedAdmin}
                onChange={(e) => setSelectedAdmin(e.target.value)}
                style={{
                  width: "100%",
                  padding: "12px",
                  borderRadius: "12px",
                  border: "1px solid #e2e8f0",
                  backgroundColor: "#f8fafc",
                  fontSize: "14px",
                  fontFamily: "inherit",
                  transition: "all 0.3s ease",
                }}
              >
                <option value="">Select Admin</option>
                {Array.isArray(admins) && admins.length > 0 ? (
                  admins.map((admin) => (
                    <option key={admin._id} value={admin._id}>
                      {admin.fullName} - {admin.email}
                    </option>
                  ))
                ) : (
                  <option disabled>No admins available</option>
                )}
              </select>
            </ArgonBox>
          </PremiumCard>

          <ArgonBox display="flex" justifyContent="flex-end" gap={2}>
            <ArgonButton
              variant="outlined"
              color="secondary"
              onClick={() => {
                setStatusDialog(false);
                setSelectedTicket(null);
              }}
            >
              Cancel
            </ArgonButton>
            <ArgonButton
              variant="contained"
              color="success"
              type="submit"
              disabled={updatingStatus}
            >
              {updatingStatus ? <CircularProgress size={20} color="inherit" /> : "Save Changes"}
            </ArgonButton>
          </ArgonBox>
        </form>
      </PremiumDialog>

      {/* ==================== MESSAGE DIALOG ==================== */}
      <PremiumDialog
        open={messageDialog}
        onClose={() => {
          setMessageDialog(false);
          setSelectedTicket(null);
          setTicketMessages([]);
          setNewMessage("");
        }}
        title={`Messages - ${selectedTicket?.subject || ""}`}
        icon={ChatBubbleOutline}
        maxWidth="md"
        headerGradient="linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)"
      >
        {/* Messages Container */}
        <ArgonBox
          sx={{
            height: "400px",
            overflowY: "auto",
            p: 2,
            backgroundColor: "#f8fafc",
            borderRadius: "16px",
            mb: 3,
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-track": {
              background: "#e2e8f0",
              borderRadius: "10px",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "#667eea",
              borderRadius: "10px",
              "&:hover": {
                background: "#5568d3",
              },
            },
          }}
        >
          {loadingMessages ? (
            <ArgonBox display="flex" justifyContent="center" alignItems="center" height="100%">
              <CircularProgress sx={{ color: "#667eea" }} />
            </ArgonBox>
          ) : ticketMessages.length > 0 ? (
            ticketMessages.map((msg, index) => (
              <MessageBubble
                key={index}
                message={{
                  senderName:
                    msg.from === "ADMIN"
                      ? msg.userId?.fullName || "Admin"
                      : msg.parentId?.fullName || msg.tutorId?.fullName || "Client",
                  text: msg.message,
                  timestamp: new Date(msg.createdAt).toLocaleString(),
                }}
                isAdmin={msg.from === "ADMIN"}
              />
            ))
          ) : (
            <ArgonBox
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              height="100%"
            >
              <ChatBubbleOutline sx={{ fontSize: 64, color: "#cbd5e1", mb: 2 }} />
              <ArgonTypography variant="h6" color="#64748b">
                No messages yet
              </ArgonTypography>
              <ArgonTypography variant="body2" color="#94a3b8">
                Start the conversation!
              </ArgonTypography>
            </ArgonBox>
          )}
        </ArgonBox>

        {/* Message Input */}
        <PremiumCard elevated sx={{ p: 2 }}>
          <ArgonBox display="flex" gap={2}>
            <ArgonInput
              placeholder="Type your message..."
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
              multiline
              rows={2}
              fullWidth
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                },
              }}
            />
            <ArgonButton
              variant="contained"
              color="info"
              onClick={handleSendMessage}
              disabled={!newMessage.trim() || sendingMessage}
              sx={{
                minWidth: "100px",
                height: "fit-content",
                alignSelf: "flex-end",
              }}
            >
              {sendingMessage ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <>
                  <SendIcon sx={{ mr: 1 }} />
                  Send
                </>
              )}
            </ArgonButton>
          </ArgonBox>
        </PremiumCard>
      </PremiumDialog>
    </ArgonBox>
  );
}

export default communication;

